import {announce} from '@github-ui/aria-live'
import {spaceSizeExceeded} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {
  CopilotChatRepo,
  CopilotSpaceBaseRole,
  CopilotSpaceId,
  CopilotSpaceResource,
} from '@github-ui/copilot-chat-types'
import {SpaceLoader} from '@github-ui/copilot-spaces/components/SpaceLoader'
import {ReferenceSizeExceededBanner} from '@github-ui/copilot-spaces/components/ReferenceSizeExceededBanner'
import {AddAttachmentMenu} from '@github-ui/copilot-spaces/components/space-administration/AddAttachmentMenu'
import {InlineEditableText} from '@github-ui/copilot-spaces/components/space-administration/InlineEditableText'
import {ReferencesTable} from '@github-ui/copilot-spaces/components/space-administration/ReferencesTable'
import {SpaceInstructions} from '@github-ui/copilot-spaces/components/space-administration/SpaceInstructions'
import {getSpacesFilePickerOwner} from '@github-ui/copilot-spaces/components/SpacesFilePicker'
import {useFetchCopilotSpace, useFetchVisibilitySettings} from '@github-ui/copilot-spaces/hooks'
import {useDeleteCopilotSpaceResources} from '@github-ui/copilot-spaces/hooks/use-delete-copilot-space-resources-no-chat-state'
import {useUpsertCopilotSpace} from '@github-ui/copilot-spaces/hooks/use-upsert-copilot-space-no-chat-state'
import type {CopilotSpacesConfigPayload} from '@github-ui/copilot-spaces/types'
import {mergeAndDedupResources} from '@github-ui/copilot-spaces/utils/merge-and-dedup-resources'
import {getOutOfScopeResources} from '@github-ui/copilot-spaces/utils/space-source-scope'
import {ContentView} from '@github-ui/pacer/ContentView'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {FileIcon, PlusIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, Button, Stack} from '@primer/react'
import {useEffect, useRef, useState} from 'react'

import {AgentsSpaceHeader} from './AgentsSpaceHeader'
import styles from './AgentsSpacesPage.module.css'

const BASE_ROLE_OPTIONS: Array<{text: string; value: CopilotSpaceBaseRole}> = [
  {text: 'Admin', value: 'custom_copilot_admin'},
  {text: 'Editor', value: 'custom_copilot_writer'},
  {text: 'Viewer', value: 'custom_copilot_reader'},
  {text: 'No access', value: 'none'},
]

interface AgentsSpaceDetailViewProps {
  owner: string
  number: number
}

/**
 * Administration-only Space detail: name, description, instructions, sources/resources,
 * visibility, and space actions (duplicate/star/delete). Deliberately does not render
 * ChatInput, the Conversations tab, thread history, chat submission, or any
 * quota/streaming UI — this surface never lets an admin chat with the space.
 */
export function AgentsSpaceDetailView({owner, number}: AgentsSpaceDetailViewProps) {
  const spaceId: CopilotSpaceId = {id: number, owner}
  const {data: space, isPending, error: fetchError} = useFetchCopilotSpace(spaceId)
  const {copilotSpacesConfig} = useAppPayload<CopilotSpacesConfigPayload>()
  const {upsertCopilotSpace, isPending: isSaving} = useUpsertCopilotSpace(spaceId)
  const {deleteCopilotSpaceResources} = useDeleteCopilotSpaceResources(spaceId)
  const {data: visibility} = useFetchVisibilitySettings(owner, number)

  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const [recentlyUsedRepo, setRecentlyUsedRepo] = useState<CopilotChatRepo | null>(null)
  const [actionError, setActionError] = useState<{
    field: 'name' | 'description' | 'resources' | 'visibility'
    message: string
  } | null>(null)
  const resourcesRef = useRef<CopilotSpaceResource[]>([])

  useEffect(() => {
    if (space) resourcesRef.current = space.resources
  }, [space])

  if (isPending) return <SpaceLoader variant="administration" />
  if (fetchError || !space) return <p>Unable to load this space.</p>

  const visibleResources = space.resources.filter(resource => !resource.markedForDestroy)
  const currentResourceCount = visibleResources.length
  const currentSpaceSizePercentage = visibleResources.reduce(
    (total, resource) => total + (resource.sizePercentage ?? 0),
    0,
  )
  const referenceSizeExceeded = spaceSizeExceeded(space)
  const filePickerOwner = getSpacesFilePickerOwner(space)
  const outOfScopeOwners = Array.from(
    new Set(getOutOfScopeResources(space).flatMap(resource => resource.ownerLogin ?? [])),
  )

  const handleMutationError = (field: 'name' | 'description' | 'resources' | 'visibility', error: unknown) => {
    const message = error instanceof Error ? error.message : `Unable to update ${field}.`
    setActionError({field, message})
    announce(message)
  }

  const updateName = async (name: string) => {
    if (!name) return
    try {
      await upsertCopilotSpace({name})
      setActionError(null)
    } catch (error) {
      handleMutationError('name', error)
    }
  }
  const updateDescription = async (description: string) => {
    try {
      await upsertCopilotSpace({description})
      setActionError(null)
    } catch (error) {
      handleMutationError('description', error)
    }
  }

  const removeResources = async (resources: CopilotSpaceResource[]) => {
    const previousResources = resourcesRef.current
    resourcesRef.current = previousResources.filter(
      existing => !resources.some(resource => resource.id === existing.id),
    )
    try {
      const updatedSpace = await deleteCopilotSpaceResources(resources)
      if (updatedSpace) resourcesRef.current = updatedSpace.resources
      setActionError(null)
    } catch (error) {
      resourcesRef.current = previousResources
      handleMutationError('resources', error)
    }
  }

  const addResources = async (resources: CopilotSpaceResource[]) => {
    const previousResources = resourcesRef.current
    const updatedResources = mergeAndDedupResources(previousResources, resources)
    resourcesRef.current = updatedResources
    try {
      const updatedSpace = await upsertCopilotSpace({resources: updatedResources})
      resourcesRef.current = updatedSpace.resources
      setActionError(null)
    } catch (error) {
      resourcesRef.current = previousResources
      handleMutationError('resources', error)
    }
  }

  const updateResource = async (resource: CopilotSpaceResource) => {
    const previousResources = resourcesRef.current
    const updatedResources = previousResources.map(existing => (existing.id === resource.id ? resource : existing))
    resourcesRef.current = updatedResources
    try {
      const updatedSpace = await upsertCopilotSpace({resources: updatedResources})
      resourcesRef.current = updatedSpace.resources
      setActionError(null)
    } catch (error) {
      resourcesRef.current = previousResources
      handleMutationError('resources', error)
    }
  }

  const updateBaseRole = async (baseRole: CopilotSpaceBaseRole) => {
    try {
      await upsertCopilotSpace({baseRole})
      setActionError(null)
    } catch (error) {
      handleMutationError('visibility', error)
    }
  }

  return (
    <div className={styles.detail}>
      <AgentsSpaceHeader copilot={space} />
      <ContentView.Container width="small">
        <ContentView.Header>
          <ContentView.Title>
            {space.editable ? (
              <InlineEditableText
                ariaPrefix="Space name:"
                className={styles.titleText}
                value={space.name}
                onChange={name => void updateName(name)}
              />
            ) : (
              space.name
            )}
          </ContentView.Title>
          <div className={styles.description}>
            {space.editable ? (
              <InlineEditableText
                className={styles.descriptionText}
                placeholder="Add a short description to explain this space's purpose (e.g., 'Frontend design system' or 'API docs hub')."
                ariaPrefix="Space description:"
                value={space.description || ''}
                maxLength={copilotSpacesConfig.maxDescriptionLength}
                onChange={description => void updateDescription(description)}
              />
            ) : (
              space.description
            )}
          </div>
        </ContentView.Header>

        <ContentView.Section>
          <Stack gap="spacious" align="stretch">
            {actionError && (
              <Banner variant="critical" title="Unable to update space" description={actionError.message} />
            )}
            {uploadErrors.length > 0 && (
              <Banner
                variant="critical"
                title="Error during attachment upload"
                description={Array.from(new Set(uploadErrors)).join(' ')}
                onDismiss={() => setUploadErrors([])}
              />
            )}
            {referenceSizeExceeded && <ReferenceSizeExceededBanner />}
            {outOfScopeOwners.length > 0 && (
              <Banner
                variant="warning"
                title="This space can't be used in chat"
                description={`It includes private sources from ${new Intl.ListFormat().format(outOfScopeOwners)}. Only public repositories and private repositories owned by ${space.owner} work in chat. Remove those sources to use this space.`}
              />
            )}

            <SpaceInstructions
              copilotSpace={space}
              onSave={instructions => upsertCopilotSpace({generalInstructions: instructions})}
              isSaving={isSaving}
            />

            <div className={styles.sourcesSection}>
              <Stack direction="horizontal" justify="space-between" align="center" className={styles.sectionHeader}>
                <h2 className="f5 mb-0">Sources</h2>
                {space.editable && (
                  <>
                    {currentResourceCount >= copilotSpacesConfig.maxResourceCount ? (
                      <Button
                        leadingVisual={PlusIcon}
                        onClick={() =>
                          handleMutationError(
                            'resources',
                            new Error(`You can select a maximum of ${copilotSpacesConfig.maxResourceCount} sources`),
                          )
                        }
                      >
                        Add sources
                      </Button>
                    ) : (
                      <AddAttachmentMenu
                        sizePercentage={currentSpaceSizePercentage}
                        owner={filePickerOwner}
                        spaceOwner={space.owner}
                        onResourcesAdded={resources => void addResources(resources)}
                        resourcesToSave={space.resources}
                        resourceCounts={{
                          currentResourceCount,
                          maxResourceCount: copilotSpacesConfig.maxResourceCount,
                        }}
                        onError={setUploadErrors}
                        initialRepo={recentlyUsedRepo}
                        onRepoSelect={setRecentlyUsedRepo}
                        onUploadingChange={uploading => {
                          if (uploading) setUploadErrors([])
                        }}
                      >
                        <Button leadingVisual={PlusIcon}>Add sources</Button>
                      </AddAttachmentMenu>
                    )}
                  </>
                )}
              </Stack>
              <div className={styles.sectionContent}>
                {visibleResources.length > 0 ? (
                  <ReferencesTable
                    owner={filePickerOwner}
                    resources={visibleResources}
                    resourceCounts={{
                      currentResourceCount,
                      maxResourceCount: copilotSpacesConfig.maxResourceCount,
                    }}
                    sizePercentage={currentSpaceSizePercentage}
                    onDeleteResources={resources => void removeResources(resources)}
                    onUpdateResource={resource => void updateResource(resource)}
                    onResourcesAdded={resources => void addResources(resources)}
                    readOnly={!space.editable}
                  />
                ) : (
                  <div className="Box p-6 text-center">
                    <FileIcon size={24} className="fgColor-muted mb-2" />
                    <h2 className="f4">{space.editable ? 'Add sources to get started' : 'Welcome'}</h2>
                    <p className="fgColor-muted mb-0">
                      {space.editable
                        ? 'Provide files, docs, issues, or repositories so Copilot can give more relevant answers.'
                        : `This space doesn't have any sources yet. Someone with edit access can add them.`}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className={styles.visibilitySection}>
              <h2 className="f5 mb-0">Visibility</h2>
              {space.adminable ? (
                <BaseRoleMenu currentBaseRole={space.baseRole} onSelect={role => void updateBaseRole(role)} />
              ) : (
                <p>{BASE_ROLE_OPTIONS.find(option => option.value === space.baseRole)?.text ?? space.baseRole}</p>
              )}
              {visibility?.memberCount != null && space.ownerIsOrg && <p>{visibility.memberCount} members</p>}
            </div>
          </Stack>
        </ContentView.Section>
      </ContentView.Container>
    </div>
  )
}

function BaseRoleMenu({
  currentBaseRole,
  onSelect,
}: {
  currentBaseRole: CopilotSpaceBaseRole
  onSelect: (role: CopilotSpaceBaseRole) => void
}) {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLButtonElement>(null)
  const current = BASE_ROLE_OPTIONS.find(option => option.value === currentBaseRole)

  return (
    <ActionMenu open={open} onOpenChange={setOpen} anchorRef={anchorRef}>
      <ActionMenu.Button ref={anchorRef}>{current?.text ?? currentBaseRole}</ActionMenu.Button>
      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          {BASE_ROLE_OPTIONS.map(option => (
            <ActionList.Item
              key={option.value}
              selected={option.value === currentBaseRole}
              onSelect={() => {
                onSelect(option.value)
                setOpen(false)
              }}
            >
              {option.text}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
